import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import LyricScroller from './LyricScroller';
import YouTubeReference from './YouTubeReference';

/**
 * One song: hear the original, practise over the instrumental with the lyrics
 * tracking whichever one is playing, then record.
 *
 * The lyrics need a clock. Whichever player last reported a position owns the
 * highlight — without that the LRC renders but never advances, which reads as
 * "the highlight is broken".
 */
export default function SongStage({ song, currentUser }) {
  const [time, setTime] = useState(0);
  const [source, setSource] = useState(null); // 'original' | 'practice'
  // Learn the tune with the vocal in, then drop it and sing the same part.
  const [mode, setMode] = useState('full');   // 'full' | 'backing'
  const practiceRef = useRef(null);

  const fullUrl = song.guide_url;
  const backingUrl = song.instrumental_url;
  const practiceUrl = mode === 'full' ? (fullUrl || backingUrl) : (backingUrl || fullUrl);

  useEffect(() => {
    setTime(0);
    setSource(null);
    setMode(song.guide_url ? 'full' : 'backing');
  }, [song.id, song.guide_url]);

  // Switching between the two keeps the playhead, so you can hear how a line
  // goes and immediately try it yourself from the same spot.
  const pendingSeek = useRef(null);

  const switchMode = (next) => {
    if (next === mode) return;
    const audio = practiceRef.current;
    if (audio) pendingSeek.current = { at: audio.currentTime, wasPlaying: !audio.paused };
    setMode(next);
  };

  // The restore has to wait for the new source's metadata, and with
  // preload="none" that never arrives on its own — load() has to be called
  // explicitly, or currentTime is silently ignored and the track restarts.
  useEffect(() => {
    const audio = practiceRef.current;
    const pending = pendingSeek.current;
    if (!audio || !pending) return undefined;
    pendingSeek.current = null;

    const restore = () => {
      try { audio.currentTime = pending.at; } catch (e) { /* seek refused */ }
      if (pending.wasPlaying) audio.play().catch(() => {});
    };
    audio.addEventListener('loadedmetadata', restore, { once: true });
    audio.load();
    return () => audio.removeEventListener('loadedmetadata', restore);
  }, [practiceUrl]);

  useEffect(() => {
    const audio = practiceRef.current;
    if (!audio) return undefined;
    const onTime = () => { setSource('practice'); setTime(audio.currentTime); };
    audio.addEventListener('timeupdate', onTime);
    return () => audio.removeEventListener('timeupdate', onTime);
  }, [song.id, song.instrumental_url]);

  const fromYouTube = (seconds) => { setSource('original'); setTime(seconds); };

  const hasLrc = !!song.lrc;

  return (
    <section className="gs-sing">
      <div className="gs-sing__reference">
        <h4>1. Listen to the original</h4>
        {song.youtube_id ? (
          <YouTubeReference videoId={song.youtube_id} title={song.title} onTime={fromYouTube} />
        ) : (
          <p className="gs-karaoke__empty">
            The full version of this song isn't published yet — you can still practise
            with the lyrics and backing track below.
          </p>
        )}
      </div>

      <div className="gs-sing__practice">
        <h4>2. Practise</h4>
        {practiceUrl ? (
          <>
            <div className="gs-sing__modes">
              <button
                type="button"
                className={mode === 'full' ? 'gs-chip is-active' : 'gs-chip'}
                onClick={() => switchMode('full')}
                disabled={!fullUrl}
              >
                Full song
              </button>
              <button
                type="button"
                className={mode === 'backing' ? 'gs-chip is-active' : 'gs-chip'}
                onClick={() => switchMode('backing')}
                disabled={!backingUrl}
              >
                Backing track only
              </button>
            </div>
            <p className="gs-studio__hint">
              {mode === 'full'
                ? 'Singer included — learn how the line goes.'
                : 'Vocal removed — your turn. Switching keeps your place in the song.'}
            </p>
            <audio
              ref={practiceRef}
              className="gs-studio__player"
              src={practiceUrl}
              controls
              preload="none"
            />
          </>
        ) : (
          <p className="gs-karaoke__empty">
            No audio for this song yet — you can still record a cappella.
          </p>
        )}

        {hasLrc && (
          <p className="gs-sing__cue">
            {source
              ? `Lyrics following the ${source === 'original' ? 'original video' : (mode === 'full' ? 'full song' : 'backing track')}`
              : 'Press play above and the lyrics will follow along'}
          </p>
        )}
        <LyricScroller lrc={song.lrc} plain={song.lyrics} time={time} />
      </div>

      <div className="gs-sing__record">
        <h4>3. Record your version</h4>
        {!currentUser ? (
          <p className="gs-karaoke__empty">Sign in to record and save a take.</p>
        ) : (
          <>
            {/* The studio is its own route now, not a panel that unfolds inside this
                page. Singing wants the whole screen for the live wave and score, and
                a URL a singer can return to. */}
            <Link className="gs-btn gs-btn--record" to={`/studio/${song.slug || song.id}`}>
              Open the studio ›
            </Link>
            <p className="gs-studio__hint">
              You'll be scored live on pitch, timing and how much of the song you sing.
            </p>
          </>
        )}
        {song.usage_terms && (
          <p className="gs-studio__hint gs-sing__terms">{song.usage_terms}</p>
        )}
      </div>
    </section>
  );
}
